/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import {
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import moment from "moment";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import { LoadingButton } from "@mui/lab";
import { getLedgerList, getVendorList } from "app/redux/slice/vendorSlice";
import { getPlatformList } from "app/redux/slice/platformSlice";

const validationSchema = yup.object().shape({
  // vendor: yup.object().required("Please select a vendor").nullable(),
  // platform: yup.object().required("Please select a platform").nullable(),
  vendor: yup
    .object()
    .nullable()
    .when("type", {
      is: "vendor",
      then: (schema) => schema.required("Please select a vendor"),
      otherwise: (schema) => schema.nullable(),
    }),

  platform: yup
    .object()
    .nullable()
    .when("type", {
      is: "platform",
      then: (schema) => schema.required("Please select a platform"),
      otherwise: (schema) => schema.nullable(),
    }),
  toDate: yup.date().when("fromDate", (fromDate, schema) => {
    if (fromDate) {
      return schema
        .required("To Date is required")
        .min(fromDate, "To Date cannot be before From Date");
    }
    return schema;
  }),
});

const Ledger = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const vendorData = useSelector((state) => state?.vendor);
  const platformData = useSelector((state) => state?.platform);

  const [data, setData] = useState([]);
  const [view, setView] = useState(false);

  const getData = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const value = {
      fromDate: values?.fromDate,
      toDate: values?.toDate,
      type: values.type,
    };

    const response = await dispatch(
      getLedgerList({
        data: value,
        id:
          values.type === "vendor"
            ? values?.vendor?._id
            : values?.platform?._id,
      })
    ).unwrap();
    if (response?.status === 200) {
      const tempObj = response?.data?.data?.ledger;
      const greater =
        tempObj?.debit?.length > tempObj?.credit?.length
          ? tempObj?.debit.length
          : tempObj?.credit.length;
      let tempArr = [];

      for (let i = 0; i <= greater - 1; i++) {
        let temp = {};
        temp.debit_date = tempObj?.debit[i] ? tempObj?.debit[i].date : "";
        temp.debit_des = tempObj?.debit[i] ? tempObj?.debit[i].description : "";
        temp.debit = tempObj?.debit[i] ? +tempObj?.debit[i].amount : "";

        temp.credit_date = tempObj?.credit[i] ? tempObj?.credit[i].date : "";
        temp.credit_des = tempObj?.credit[i]
          ? tempObj?.credit[i].description
          : "";
        temp.credit = tempObj?.credit[i] ? +tempObj?.credit[i].amount : "";

        tempArr.push(temp);
      }
      tempObj?.closingBalance?.forEach((item) => {
        let temp = {};
        temp.debit_date = item.type !== "credit" ? item.date : "";
        temp.debit_des = item.type !== "credit" ? item.description : "";
        temp.debit = item.type !== "credit" ? +item.amount : "";

        temp.credit_date = item.type === "credit" ? item.date : "";
        temp.credit_des = item.type === "credit" ? item.description : "";
        temp.credit = item.type === "credit" ? +item.amount : "";

        tempArr.push(temp);
      });
      setData(tempArr);
      setView(true);
      setSubmitting(false);
    } else {
      enqueueSnackbar(response.data?.response_message, { variant: "error" });
    }
  };

  const formik = useFormik({
    initialValues: {
      type: "vendor",
      fromDate: "",
      toDate: "",
      vendor: null,
      platform: null,
    },
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: getData,
  });
  const { values, setFieldValue, handleSubmit, isSubmitting, errors } = formik;

  useEffect(() => {
    dispatch(getVendorList());
    dispatch(getPlatformList());
  }, []);
  const totalDebit = data.reduce((sum, item) => sum + +item.debit ?? 0, 0);
  const totalCredit = data.reduce((sum, item) => sum + +item.credit ?? 0, 0);

  return (
    <>
      <Card sx={{ display: "flex", mb: 3 }}>
        <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
          <CardContent>
            <FormikProvider value={formik}>
              <Form noValidate autoComplete="off">
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel id="type-label">Type*</InputLabel>
                      <Select
                        name="createFor"
                        labelId="type-label"
                        id="createFor"
                        size="small"
                        value={values.type}
                        label="Type*"
                        onChange={(e) => {
                          setFieldValue("type", e.target.value);
                          setFieldValue("vendor", null);
                          setFieldValue("platform", null);
                          setView(false);
                        }}
                      >
                        <MenuItem value="vendor">Vendor</MenuItem>
                        <MenuItem value="platform">Platform</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {values.type === "platform" ? (
                    <Grid item xs={12} sm={3}>
                      <JumboAutoComplete
                        options={platformData?.list ?? []}
                        label="Platform*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name="platform"
                        value={values?.platform}
                        onChange={(evt, value) => {
                          setFieldValue("platform", value);
                          setView(false);
                        }}
                        error={errors?.platform}
                        helperText={errors?.platform}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={3}>
                      <JumboAutoComplete
                        options={vendorData?.list ?? []}
                        label="Vendor*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name="vendor"
                        value={values?.vendor}
                        onChange={(evt, value) => {
                          setFieldValue("vendor", value);
                          setView(false);
                        }}
                        error={errors?.vendor}
                        helperText={errors?.vendor}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={2.5}>
                    <JumboTextField
                      type="date"
                      label={"From Date"}
                      name="fromDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      value={values?.fromDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2.5}>
                    <JumboTextField
                      type="date"
                      label={"To Date"}
                      name="toDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      value={values?.toDate}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <LoadingButton
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleSubmit();
                      }}
                      loading={isSubmitting}
                      loadingIndicator={<CircularProgress size={20} />}
                    >
                      Submit
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </Div>
      </Card>
      {view && (
        <Card>
          <CardContent>
            {data.length === 0 ? (
              <JumboListNoDataPlaceholder />
            ) : (
              <TableContainer component={Paper} sx={{ margin: "auto" }}>
                <Typography variant="h5" align="center" sx={{ mt: 2, mb: 2 }}>
                  {values?.vendor?.first_name} {values?.vendor?.last_name}
                  {" Ledger "}
                  {values.fromDate && values.toDate && (
                    <>
                      {`: ${moment(values.fromDate, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )} - ${moment(values.toDate, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )}`}
                    </>
                  )}
                </Typography>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong> Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong> Description</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "2px solid #cfc5c5" }}>
                        <strong>Debit</strong>
                      </TableCell>
                      <TableCell sx={{ borderLeft: "2px solid #cfc5c5" }}>
                        <strong> Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong> Description</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Credit</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.debit_date
                            ? moment(item.debit_date).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>{item.debit_des}</TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #cfc5c5",
                            color: "red",
                          }}
                        >
                          {item.debit}
                        </TableCell>

                        <TableCell sx={{ borderLeft: "2px solid #cfc5c5" }}>
                          {item.credit_date
                            ? moment(item.credit_date).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>{item.credit_des}</TableCell>
                        <TableCell sx={{ color: "green" }}>
                          {item.credit}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                      >
                        {totalDebit}
                      </TableCell>

                      <TableCell
                        colSpan={2}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                          borderLeft: "2px solid #cfc5c5",
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                      >
                        {totalCredit}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default Ledger;
